import { useState } from "react";
import { Plus, Pencil, Trash2, Trophy, IndianRupee, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DataTable,
  FormInput,
  PageHeader,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import {
  useAdminRankRewards,
  useCreateRankRewardMutation,
  useUpdateRankRewardMutation,
  useDeleteRankRewardMutation,
} from "@/hooks/queries";
import type { RankRewardDto } from "@/types/rank-reward.dto";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

const emptyForm = {
  rank: "",
  selfBusiness: 0,
  powerLegBusiness: 0,
  otherLegBusiness: 0,
  rewardAmount: 0,
};

const AdminRankRewards = () => {
  const { toast } = useToast();
  const { data: rewards = [], isLoading } = useAdminRankRewards();
  const createMutation = useCreateRankRewardMutation();
  const updateMutation = useUpdateRankRewardMutation();
  const deleteMutation = useDeleteRankRewardMutation();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editing, setEditing] = useState<RankRewardDto | null>(null);
  const [form, setForm] = useState(emptyForm);

  const table = useDataTable<RankRewardDto>({
    data: rewards,
    searchKeys: ["rank"],
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setSheetOpen(true);
  };

  const openEdit = (row: RankRewardDto) => {
    setEditing(row);
    setForm({
      rank: row.rank,
      selfBusiness: row.selfBusiness,
      powerLegBusiness: row.powerLegBusiness,
      otherLegBusiness: row.otherLegBusiness,
      rewardAmount: row.rewardAmount,
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, dto: form });
        toast({ title: "Rank reward updated" });
      } else {
        await createMutation.mutateAsync(form);
        toast({ title: "Rank reward created" });
      }
      setSheetOpen(false);
    } catch (err) {
      toast({ title: "Error", description: getApiErrorMessage(err), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await deleteMutation.mutateAsync(deleteId);
      toast({ title: "Rank reward permanently deleted" });
    } catch (err) {
      toast({ title: "Error", description: getApiErrorMessage(err), variant: "destructive" });
    } finally {
      setDeleteId(null);
    }
  };

  const columns: DataTableColumn<RankRewardDto>[] = [
    {
      id: "rank",
      header: "Rank",
      accessorKey: "rank",
      sortable: true,
      mobilePriority: 1,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-yellow-500" />
          <span className="font-semibold">{row.rank}</span>
        </div>
      ),
    },
    {
      id: "selfBusiness",
      header: "Self BV",
      accessorKey: "selfBusiness",
      sortable: true,
      align: "right",
      mobilePriority: 2,
      cell: (row) => (
        <Badge variant="outline" className="font-mono">
          {row.selfBusiness.toLocaleString()} BV
        </Badge>
      ),
    },
    {
      id: "powerLegBusiness",
      header: "Power BV",
      accessorKey: "powerLegBusiness",
      sortable: true,
      align: "right",
      mobilePriority: 3,
      cell: (row) => (
        <Badge variant="outline" className="font-mono text-blue-600 border-blue-300">
          {row.powerLegBusiness.toLocaleString()} BV
        </Badge>
      ),
    },
    {
      id: "otherLegBusiness",
      header: "Other BV",
      accessorKey: "otherLegBusiness",
      sortable: true,
      align: "right",
      hideOnMobile: true,
      cell: (row) => (
        <Badge variant="outline" className="font-mono text-purple-600 border-purple-300">
          {row.otherLegBusiness.toLocaleString()} BV
        </Badge>
      ),
    },
    {
      id: "rewardAmount",
      header: "Reward (INR)",
      accessorKey: "rewardAmount",
      sortable: true,
      align: "right",
      mobilePriority: 4,
      cell: (row) => (
        <span className="font-bold text-green-600">
          ₹{row.rewardAmount.toLocaleString()}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      align: "center",
      cell: (row) => (
        <div className="flex gap-1 justify-center">
          <Button
            id={`btn-edit-rank-${row.id}`}
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => openEdit(row)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            id={`btn-delete-rank-${row.id}`}
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={() => setDeleteId(row.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Rank Rewards" description="Manage MLM rank reward milestones." />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rank Rewards"
        description="Define rank milestones with Self, Power Leg, and Other Leg B.V. requirements."
        actions={
          <Button id="btn-add-rank" onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Rank
          </Button>
        }
      />

      <DataTable
        title="All Rank Rewards"
        columns={columns}
        data={table.rows}
        getRowId={(row) => String(row.id)}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search by rank name..."
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        emptyMessage="No rank rewards configured. Add your first one to get started."
        renderMobileCard={(row) => (
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-yellow-500" />
                <p className="font-semibold text-sm">{row.rank}</p>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Self: {row.selfBusiness} BV · Power: {row.powerLegBusiness} BV
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className="font-bold text-green-600 text-sm">₹{row.rewardAmount.toLocaleString()}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(row)}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => setDeleteId(row.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        )}
      />

      {/* Add / Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              {editing ? "Edit Rank Reward" : "New Rank Reward"}
            </SheetTitle>
            <SheetDescription>
              Set the B.V. requirements and INR reward amount for this rank milestone.
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 py-6">
            <FormInput
              id="field-rank-name"
              label="Rank Name"
              placeholder="e.g. Silver, Gold, Diamond"
              value={form.rank}
              onChange={(e) => setForm((f) => ({ ...f, rank: e.target.value }))}
            />

            <div className="rounded-lg border border-dashed p-4 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Zap className="h-3.5 w-3.5" /> Business Volume Requirements (B.V.)
              </p>
              <FormInput
                id="field-self-bv"
                label="Self Business (B.V.)"
                type="number"
                placeholder="0"
                value={String(form.selfBusiness)}
                onChange={(e) => setForm((f) => ({ ...f, selfBusiness: Number(e.target.value) }))}
              />
              <FormInput
                id="field-power-bv"
                label="Power Leg Business (B.V.)"
                type="number"
                placeholder="0"
                value={String(form.powerLegBusiness)}
                onChange={(e) => setForm((f) => ({ ...f, powerLegBusiness: Number(e.target.value) }))}
              />
              <FormInput
                id="field-other-bv"
                label="Other Leg Business (B.V.)"
                type="number"
                placeholder="0"
                value={String(form.otherLegBusiness)}
                onChange={(e) => setForm((f) => ({ ...f, otherLegBusiness: Number(e.target.value) }))}
              />
            </div>

            <div className="rounded-lg border border-dashed p-4 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <IndianRupee className="h-3.5 w-3.5" /> Reward Amount (INR)
              </p>
              <FormInput
                id="field-reward-amount"
                label="Reward Amount (₹)"
                type="number"
                placeholder="0"
                value={String(form.rewardAmount)}
                onChange={(e) => setForm((f) => ({ ...f, rewardAmount: Number(e.target.value) }))}
              />
            </div>
          </div>

          <SheetFooter>
            <Button
              id="btn-save-rank-reward"
              onClick={handleSave}
              disabled={isSaving || !form.rank.trim()}
              className="w-full"
            >
              {isSaving ? "Saving…" : editing ? "Update Rank Reward" : "Create Rank Reward"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirm */}
      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently Delete Rank Reward?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The rank reward will be permanently removed from the
              database. Any existing reward requests linked to this rank may be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              id="btn-confirm-delete-rank"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminRankRewards;
